import React from 'react';
import { onAuthStateChanged, User, updateProfile, signInAnonymously } from 'firebase/auth';
import { auth, analytics } from './config';
import { setUserId, setUserProperties, logEvent } from 'firebase/analytics';
import { httpsCallable } from 'firebase/functions';
import { functions } from './config';
import { loginWithEmail, signupWithEmail, loginWithGoogle, logout as authLogout, resetPassword } from './authentication';
import { resolveUserProfiles, probeIsAdmin } from './dataAccess';

export type AppUser = User & { admin?: boolean };

export interface AuthContextValue {
  currentUser: AppUser | null;
  loading: boolean;
  signup: (email: string, password: string) => Promise<User>;
  login: (email: string, password: string) => Promise<User>;
  loginWithGoogle: () => Promise<User>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  reloadUser: () => Promise<void>;
}

const AuthContext = React.createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = React.useState<AppUser | null>(null);
  const [loading, setLoading] = React.useState<boolean>(true);

  React.useEffect(() => {
    let triedAnon = false;
    const unsub = onAuthStateChanged(auth, async (user) => {
      setLoading(false);
      const setSignedOutAnalytics = () => {
        try {
          setUserId(analytics, undefined as any);
          setUserProperties(analytics, { signed_in: 'false' });
        } catch {}
      };

      if (!user) {
        setCurrentUser(null);
        setSignedOutAnalytics();
        if (!triedAnon) {
          triedAnon = true;
          (async () => {
            try {
              await signInAnonymously(auth);
            } catch {
              // ignore since UI can still function without auth
            }
          })();
        }
        return;
      }

      const providers = user.providerData.map((p) => p?.providerId).filter(Boolean) as string[];
      const isRealUser = providers.some((id) => id !== 'anonymous');

      if (!isRealUser) {
        setCurrentUser(null);
        setSignedOutAnalytics();
        return;
      }
      setCurrentUser(user as AppUser);
      try {
        setUserId(analytics, user.uid);
        setUserProperties(analytics, {
          signed_in: 'true',
          auth_provider: providers.join(',') || 'unknown',
        });
        logEvent(analytics, 'login', {
          method: providers.join(',') || 'unknown',
        });
      } catch {}
      (async () => {
        try {
          try {
            const callable = httpsCallable(functions, 'ensureUserDocument');
            await callable({
              displayName: user.displayName ?? null,
              email: user.email ?? null,
              photoURL: user.photoURL ?? null,
            });
          } catch {}

          let url: string | null = null;
          let nameFromDoc: string | null = null;
          try {
            const map = await resolveUserProfiles([user.uid]);
            const me = map[user.uid];
            url = me?.photoURL ?? null;
            nameFromDoc = me?.displayName ?? null;
          } catch {}

          if ((!user.photoURL && url) || (!user.displayName && nameFromDoc)) {
            try {
              await updateProfile(user, {
                photoURL: user.photoURL ? user.photoURL : (url || undefined),
                displayName: user.displayName ? user.displayName : (nameFromDoc || undefined),
              });
              try { await user.reload(); } catch {}
            } catch {}
          }
          const refreshed = auth.currentUser ?? user;
          let isAdmin = false;
          try { isAdmin = await probeIsAdmin(); } catch {}
          const extended: AppUser = Object.assign(refreshed, { admin: isAdmin });
          setCurrentUser(extended);
        } catch {
          setCurrentUser(user as AppUser);
        }
      })();
    });
    return () => unsub();
  }, []);

  const value: AuthContextValue = {
    currentUser,
    loading,
    signup: async (email, password) => {
      const user = await signupWithEmail(email, password);
      return user;
    },
    login: async (email, password) => {
      const user = await loginWithEmail(email, password);
      return user;
    },
    loginWithGoogle: async () => {
      const user = await loginWithGoogle();
      return user;
    },
    logout: async () => {
      await authLogout();
    },
    resetPassword: async (email: string) => {
      await resetPassword(email);
    },
    reloadUser: async () => {
      try {
        await auth.currentUser?.reload();
      } catch {}
      setCurrentUser(auth.currentUser);
    },
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}


